import clsx from "clsx";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ApiClient } from "~/api/client";
import Button from "~/components/common/button";
import Loader from "~/components/common/loader";
import ShopsNew from "~/components/pages/product/bottom/deliveryNew/shopsNew";
import { showPopup } from "~/redux/action-creaters/popup";
import { popupTypes } from "~/redux/reducers/popupReducer";
import { DeliveryTypes } from "~/types/cart";
import { ECOMMERCE_LISTS, useEcommerce } from "~/utils/tracking";

import styles from "../deliveryNew/index.module.scss";
import Edost from "./edost";
import Sdek from "./sdek";
import { useProductDeliveryLogic } from "./useProductDeliveryLogic";

/**
 * Блок доставки на карточке товара по подходу корзины (state.cart, CONFIG-модель,
 * новые сервисы sdek/edost). Визуально повторяет старый ProductDelivery, но без
 * захардкоженных условий. Включается параметром ?isNewEdost.
 */
export default function ProductDeliveryNew({ product }: any) {
  const dispatch = useDispatch();
  const router = useRouter();

  const { edostData } = useSelector((state: any) => state.cart);
  const { shops = [] } = useSelector((state: any) => state.contacts);

  const {
    cityName,
    isListLoaded,
    deliveryListByLocation,
    currentId,
    setCurrentId,
    currentDelivery,
  } = useProductDeliveryLogic(product);

  const canBuy = product.CATALOG?.AVAILABLE;

  const { trackAdd } = useEcommerce();

  const onBuy = async () => {
    await ApiClient.basket.addProductToBasket(
      product.ID,
      product.CART_PROPS || null,
    );
    trackAdd(product, { list: ECOMMERCE_LISTS.delivery });
    router.push("/personal/cart/");
  };

  // Подпись вкладки — как раньше: edost «От X руб», самовывоз «В N пунктах»,
  // остальное — цена из CONFIG.PRICE, иначе DESCRIPTION.
  const getTabInfo = useMemo(
    () => (item: any) => {
      if (item.CONFIG?.MAP_LOGIC === "edost") {
        const companies = Array.isArray(edostData?.companies)
          ? edostData.companies
          : [];
        if (companies.length) {
          const min = Math.min(
            ...companies.map((company: any) =>
              Math.max(0, parseInt(company.price, 10) || 0),
            ),
          );
          return `От ${min} руб`;
        }
      }
      if (item.CONFIG?.MAP_LOGIC === "contacts") {
        return `В ${shops.length}-х пунктах`;
      }
      const priceNumber = parseInt(item.CONFIG?.PRICE, 10) || 0;
      if (priceNumber > 0) {
        return `${priceNumber} руб`;
      }
      return item.DESCRIPTION || "";
    },
    [edostData, shops.length],
  );

  const currentMapLogic = currentDelivery?.CONFIG?.MAP_LOGIC;
  const currentShopsType =
    currentDelivery?.CONFIG?.IS_SHOW_INSTALL === "Y"
      ? DeliveryTypes.install
      : DeliveryTypes.pickup;
  const isLoading = cityName !== "Россия" && !isListLoaded;

  return (
    <div className={styles.Delivery}>
      {cityName === "Россия" && (
        <div className="text">
          <div className="block-attention">
            <span className={styles.EmptyCity}>
              Для расчета стоимости доставки выберите город:
              <Button
                customStyles={styles.FormButton}
                label="Выбрать город"
                onClick={() => {
                  dispatch(showPopup(popupTypes.locationNew));
                }}
              />
            </span>
          </div>
        </div>
      )}
      {isLoading && (
        <div className={styles.Loader}>
          <Loader />
        </div>
      )}
      {cityName !== "Россия" && !isLoading && (
        <>
          <div className={styles.Title}>
            Доставка в г.{" "}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <span onClick={() => dispatch(showPopup(popupTypes.locationNew))}>
              {cityName}
            </span>
          </div>

          <div className={styles.Tabs} id="tabs">
            {deliveryListByLocation.map((item: any) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                key={item.ID}
                onClick={() => setCurrentId(item.ID)}
                className={clsx(
                  styles.Item,
                  currentId === item.ID && styles.Active,
                )}
              >
                <div>{item.NAME}</div>
                <span>{getTabInfo(item)}</span>
              </div>
            ))}
          </div>
          <div className={styles.Content}>
            <ShopsNew
              currentType={currentShopsType}
              productId={product.ID}
              onBuy={onBuy}
              canBuy={canBuy}
              isShow={currentMapLogic === "contacts"}
            />
            <Sdek
              delivery={currentDelivery}
              onBuy={onBuy}
              canBuy={canBuy}
              isShow={currentMapLogic === "sdek"}
            />
            <Edost
              delivery={currentDelivery}
              onBuy={onBuy}
              canBuy={canBuy}
              isShow={currentMapLogic === "edost"}
            />
          </div>
        </>
      )}
    </div>
  );
}
